/**
 * Date helpers — all browser-native, zero manual timezone math.
 *
 * Philosophy: trust the browser's local clock.
 *   • Date getters (getFullYear/getMonth/getDate/getHours/getMinutes) return
 *     the value in the browser's local timezone — which is what the user sees.
 *   • new Date("YYYY-MM-DDTHH:MM") is parsed as LOCAL time by the spec
 *     (date+time strings without a Z/offset are local; date-only strings
 *     like "YYYY-MM-DD" are UTC — always append T00:00 to force local).
 *   • .toISOString() converts back to UTC for API transport.
 *   • .toLocaleString() / .toLocaleDateString() display in the browser's TZ.
 *
 * The old approach hardcoded IST_OFFSET_MIN = 330 and did manual arithmetic.
 * That produced bugs whenever a date was on a UTC-vs-local boundary
 * (e.g. Oct 2nd 23:59 IST stored as T23:59Z = Oct 3rd 05:29 IST → calendar
 * blocked the next day as a holiday).
 */

const pad = (n: number) => String(n).padStart(2, "0");

/**
 * YYYY-MM-DD string in the browser's local timezone.
 * Use this to bucket calendar events by day.
 */
export function istYmd(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Full local date+time components — all in browser's local timezone. */
export function istParts(d: Date): {
  y: number; m: number; d: number; h: number; mn: number; s: number;
} {
  return {
    y:  d.getFullYear(),
    m:  d.getMonth() + 1,
    d:  d.getDate(),
    h:  d.getHours(),
    mn: d.getMinutes(),
    s:  d.getSeconds(),
  };
}

/**
 * "YYYY-MM-DDTHH:MM" (from <input type="datetime-local">, interpreted as
 * browser local time) → UTC ISO string for API transport.
 *
 * The browser spec says: datetime strings without a Z/offset are LOCAL.
 * So new Date("2025-10-02T23:59") is 23:59 in the user's clock, and
 * .toISOString() gives the correct UTC equivalent automatically.
 */
export function istLocalInputToUtcIso(localStr: string): string {
  return new Date(localStr).toISOString();
}

/**
 * Display a date/time in the browser's local timezone using Intl.
 * Replaces any calls that previously forced Asia/Kolkata.
 */
export function formatIst(
  d: Date | string,
  options: Intl.DateTimeFormatOptions,
): string {
  const date = typeof d === "string" ? new Date(d) : d;
  return date.toLocaleString("en-IN", options);
}
